import { Product, ProductVariant, getDisplayCategory } from '../types/product';

/**
 * Capitalize the first letter of a category name
 */
function capitalizeCategory(category: string): string {
  if (!category) return '';

  // Handle special cases
  const specialCases: { [key: string]: string } = {
    'trophy': 'Trophies',
    'medal': 'Medals',
    'plate': 'Plates',
    'ribbon': 'Ribbons',
    'other': 'Other',
  };

  const lower = category.toLowerCase();
  if (specialCases[lower]) {
    return specialCases[lower];
  }

  // Default: capitalize first letter
  return category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Convert display category back to raw category
 * (e.g., "Trophies" → "trophy")
 */
export function getRawCategory(displayCategory: string): string {
  const mapping: { [key: string]: string } = {
    'Trophies': 'trophy',
    'Medals': 'medal',
    'Plates': 'plate',
    'Ribbons': 'ribbon',
    'Other': 'other',
  };

  return mapping[displayCategory] || displayCategory.toLowerCase();
}

/**
 * Extract unique categories from products
 * Excludes ribbons by default
 */
export function extractCategories(products: Product[]): string[] {
  const allCategories = products
    .map((p) => getDisplayCategory(p))
    .filter((category) => category && category.trim() !== '')
    .filter((category) => category.toLowerCase() !== 'ribbon') // Exclude ribbons
    .map((category) => capitalizeCategory(category));

  // Remove duplicates
  const uniqueCategories = Array.from(new Set(allCategories));

  // Separate "Other" from the rest
  const otherCategories = uniqueCategories.filter((cat) => cat.toLowerCase() === 'other');
  const normalCategories = uniqueCategories.filter((cat) => cat.toLowerCase() !== 'other');

  // Sort normal categories alphabetically, then add "Other" at the end
  normalCategories.sort();

  // Add "All" at the beginning, normal categories in the middle, "Other" at the end
  return ['All', ...normalCategories, ...otherCategories];
}

/**
 * Extract unique categories from product variants
 * Excludes ribbons by default
 */
export function extractCategoriesFromVariants(variants: ProductVariant[]): string[] {
  const allCategories = variants
    .map((v) => getDisplayCategory(v.defaultVariant))
    .filter((category) => category && category.trim() !== '')
    .filter((category) => category.toLowerCase() !== 'ribbon') // Exclude ribbons
    .map((category) => capitalizeCategory(category));

  // Remove duplicates
  const uniqueCategories = Array.from(new Set(allCategories));

  // Separate "Other" from the rest
  const otherCategories = uniqueCategories.filter((cat) => cat.toLowerCase() === 'other');
  const normalCategories = uniqueCategories.filter((cat) => cat.toLowerCase() !== 'other');

  // Sort normal categories alphabetically, then add "Other" at the end
  normalCategories.sort();

  // Add "All" at the beginning, normal categories in the middle, "Other" at the end
  return ['All', ...normalCategories, ...otherCategories];
}

/**
 * Extract Galway Crystal categories (main categories + subcategories)
 * Main categories are in a fixed order, subcategories are alphabetical
 */
export function extractGalwayCategories(products: Product[]): string[] {
  // Fixed main categories in specific order
  const mainCategories = ['For Him', 'For Her', 'Glassware Bar'];

  // Extract subcategories from 'type' field
  const subcategories = Array.from(
    new Set(
      products
        .map((p) => p.type)
        .filter((type): type is string => type !== undefined && type.trim() !== '')
    )
  );

  // Sort subcategories alphabetically
  subcategories.sort();

  return ['All', ...mainCategories, ...subcategories];
}
